import type { FeatureListItem } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';

function CarouselItem({ image, link }: FeatureListItem) {
  return (
    <Tracked.InternalLink
      to={link.path}
      className="flex w-full flex-col items-center justify-center gap-2"
      variant="none"
      pagePosition="Lifestyle News Discover More"
      name="LATEST_FEATURES"
    >
      <div className="relative flex size-full min-h-[356px] min-w-[285px] max-w-[50vw] flex-col items-center justify-end md:min-h-[450px] md:min-w-[360px]">
        <Image
          src={image.url}
          className="absolute z-10 block size-full object-cover"
          alt={image.alt}
          loading="lazy"
          responsiveOptions={{ xs: { height: 672, retainRatio: true } }}
          progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
          retainRatio
        />
        <div className="absolute inset-0 z-20 bg-gradient-to-b from-transparent to-black opacity-70">
          <div className="z-30 flex h-full flex-col items-start justify-end gap-1 px-6 pb-6 text-white md:px-8 md:pb-8">
            <h4 className="text-start text-xs text-grey-hide">Feature</h4>
            <h3 className="text-[24px] leading-8 tracking-wide">{link.text}</h3>
            <div className="mt-1 flex flex-row items-center justify-center gap-2 text-nowrap text-sm text-white">
              <p>Read more</p>
              <div className="flex rotate-270 items-center justify-center">
                <ChevronIcon stroke="#FFFFFF" width="8px" height="8px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Tracked.InternalLink>
  );
}

function ViewMoreButton() {
  return (
    <div className="h-12 w-36 px-[10px] py-2 font-bold">
      <Tracked.InternalLink
        variant="transparent-white-border"
        className="flex items-center justify-center border-2"
        pagePosition="Body"
        name="DISCOVER_MORE_FEATURES"
        to={`/news/features/`}
      >
        View more
      </Tracked.InternalLink>
    </div>
  );
}

function Carousel({ items }: { items: FeatureListItem[] }) {
  return (
    <div className="custom-scrollbar flex w-full max-w-6xl items-start gap-2 self-stretch overflow-x-scroll pb-4 md:gap-6 lg:mx-auto">
      {items.map((i) => (
        <CarouselItem image={i.image} link={i.link} key={i.link.path} />
      ))}
    </div>
  );
}

export default function RecentFeatures({
  title,
  items
}: {
  items: FeatureListItem[];
  title: string;
}) {
  return (
    items.length > 0 && (
      <div className="flex w-full flex-col items-center justify-center gap-8 bg-black px-4 py-10 text-white md:gap-10 md:px-11 md:py-16">
        <h1 className="w-full text-center text-[32px] leading-10 md:text-[40px]">
          {title}
        </h1>
        <Carousel items={items} />
        <ViewMoreButton />
      </div>
    )
  );
}
